import React, { useRef, useEffect, useState } from "react";

interface FaceTrackerProps {
  basePath: string;
  showDebug?: boolean;
  className?: string;
}

// Grid configuration (must match your generated images)
const P_MIN = -15;
const P_MAX = 15;
const STEP = 3;
const SIZE = 256;

// Module-level cache: preloaded once, shared across all instances
const imageCache = new Map<string, HTMLImageElement>();

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function quantizeToGrid(val: number): number {
  const raw = P_MIN + ((val + 1) * (P_MAX - P_MIN)) / 2; // [-1,1] -> [-15,15]
  const snapped = Math.round(raw / STEP) * STEP;
  return clamp(snapped, P_MIN, P_MAX);
}

function sanitize(val: number): string {
  const str = Number(val).toFixed(1); // force one decimal, e.g. 0 -> 0.0
  return str.replace("-", "m").replace(".", "p");
}

function gridToFilename(px: number, py: number): string {
  return `gaze_px${sanitize(px)}_py${sanitize(py)}_${SIZE}.webp`;
}

function preloadGrid(basePath: string): void {
  if (imageCache.size > 0) return; // already preloaded
  for (let px = P_MIN; px <= P_MAX; px += STEP) {
    for (let py = P_MIN; py <= P_MAX; py += STEP) {
      const path = `${basePath}${gridToFilename(px, py)}`;
      if (!imageCache.has(path)) {
        const img = new window.Image();
        img.src = path;
        imageCache.set(path, img);
      }
    }
  }
}

export const FaceTracker: React.FC<FaceTrackerProps> = ({
  basePath,
  showDebug = false,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const centerFilename = gridToFilename(0, 0);
  const [currentImage, setCurrentImage] = useState<string>(
    `${basePath}${centerFilename}`,
  );
  const [isEasing, setIsEasing] = useState<boolean>(false);
  // NodeJS is not defined in browser
  const easingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setFromClient = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    const img = imgRef.current;

    if (!container || !img) return;

    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Calculate normalized coordinates [-1, 1] based on distance from center
    const nx = (clientX - centerX) / (rect.width / 2);
    const ny = (centerY - clientY) / (rect.height / 2);

    // Clamp to ensure we stay within bounds
    const clampedX = clamp(nx, -1, 1);
    const clampedY = clamp(ny, -1, 1);

    const px = quantizeToGrid(clampedX);
    const py = quantizeToGrid(clampedY);

    const filename = gridToFilename(px, py);
    const imagePath = `${basePath}${filename}`;

    setCurrentImage(imagePath);

    if (showDebug) {
      setDebugInfo(
        `Mouse: (${clientX.toFixed(0)}, ${clientY.toFixed(0)})<br/>Relative: (${(clientX - rect.left).toFixed(0)}, ${(clientY - rect.top).toFixed(0)})<br/>Normalized: (${nx.toFixed(2)}, ${ny.toFixed(2)})<br/>Grid: (${px}, ${py})<br/>Image: ${filename}`,
      );
    }
  };

  const easeToCenter = () => {
    // Clear any existing timeout
    if (easingTimeoutRef.current) {
      clearTimeout(easingTimeoutRef.current);
    }

    // Add easing class for CSS transition
    setIsEasing(true);

    // Set to center image immediately (CSS will handle the transition)
    const centerFilename = gridToFilename(0, 0);
    const centerImagePath = `${basePath}${centerFilename}`;
    setCurrentImage(centerImagePath);

    if (showDebug) {
      setDebugInfo("Easing to center...");
    }

    // Remove easing class after animation completes
    easingTimeoutRef.current = setTimeout(() => {
      setIsEasing(false);
      if (showDebug) {
        setDebugInfo("Centered");
      }
    }, 800);
  };

  const handleMouseMove = (e: MouseEvent) => {
    // Stop any ongoing easing
    if (easingTimeoutRef.current) {
      clearTimeout(easingTimeoutRef.current);
    }
    setIsEasing(false);

    setFromClient(e.clientX, e.clientY);
  };

  const handleMouseLeave = () => {
    easeToCenter();
  };

  const handleTouchMove = (e: TouchEvent) => {
    // Stop any ongoing easing
    if (easingTimeoutRef.current) {
      clearTimeout(easingTimeoutRef.current);
    }
    setIsEasing(false);

    if (e.touches && e.touches.length > 0) {
      const t = e.touches[0];
      setFromClient(t.clientX, t.clientY);
    }
  };

  useEffect(() => {
    preloadGrid(basePath);

    // Initialize at center
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      setFromClient(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    // Track pointer anywhere on the page
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("mouseleave", handleMouseLeave);

      // Clean up any ongoing timeout
      if (easingTimeoutRef.current) {
        clearTimeout(easingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`face-tracker ${isEasing ? "easing" : ""} ${className}`}
    >
      <img ref={imgRef} src={currentImage} alt="🌱" className="face-image" />

      {showDebug && (
        <div
          className="face-debug"
          dangerouslySetInnerHTML={{ __html: debugInfo }}
        />
      )}
    </div>
  );
};
